
import React, { useState, useRef, useEffect } from 'react';
import { Product, Order, OrderStatus, User, OptionValue, ProductOption, ProductAddon, CartItem, Review, QNAItem, Notice } from '../types';
import { db } from '../firebase';
import { collection, onSnapshot, query, where, orderBy, updateDoc, doc, deleteDoc, getDocs, setDoc, addDoc } from 'firebase/firestore';

interface AdminDashboardProps {
  products: Product[];
  categories: string[]; 
  onUpdateCategories: (cats: string[]) => void; 
  onAddProduct: (product: Product) => void;
  onUpdateProduct: (product: Product) => void;
  onDeleteProduct: (id: string) => void;
  users: User[];
  orders: Order[];
  onUpdateUserPoints: (userId: string, points: number) => void;
  onUpdateUser: (user: User) => void;
  onDeleteUser: (userId: string) => void;
  onUpdateOrderStatus: (orderId: string, status: OrderStatus, tracking?: { number: string, company: string }) => void;
}

const EMOJIS = [
  "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "😊", "😇", "🙂", "🙃", "😉", "😌", "😍", "🥰",
  "😘", "😗", "😙", "😚", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", "😎", "🤩", "🥳", "😏",
  "✨", "🔥", "🌈", "⭐", "🌟", "💡", "📢", "🎁", "🛒", "📦", "🚚", "🏠", "💻", "📱", "📷", "🎮",
  "✅", "❌", "⚠️", "🛑", "🔔", "❤️", "👍", "🙌", "👏", "🎉", "💯", "🚀", "🛠️", "⚙️", "🔋", "📡"
];

const BLOCKQUOTES = [
  { name: '심플 보더', html: '<blockquote style="border-left: 5px solid #6366f1; padding: 15px 20px; background: #f8fafc; margin: 20px 0; border-radius: 0 12px 12px 0; font-style: italic; color: #475569;">여기에 인용문을 입력하세요.</blockquote>' },
  { name: '따옴표 스타일', html: '<blockquote style="position: relative; padding: 30px 40px; background: #fff; border: 1px solid #e2e8f0; margin: 20px 0; border-radius: 20px; color: #1e293b;"><span style="position: absolute; top: 10px; left: 15px; font-size: 40px; color: #cbd5e1; font-family: serif;">"</span>여기에 인용문을 입력하세요.<span style="position: absolute; bottom: -10px; right: 15px; font-size: 40px; color: #cbd5e1; font-family: serif;">"</span></blockquote>' },
  { name: '강조 박스', html: '<blockquote style="background: #e0e7ff; padding: 20px; border-radius: 16px; margin: 20px 0; color: #3730a3; font-weight: bold; text-align: center;">여기에 중요한 인용구를 입력하세요.</blockquote>' },
  { name: '미니멀 센터', html: '<blockquote style="border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; padding: 25px 0; margin: 30px 0; text-align: center; font-size: 1.1em; color: #64748b; font-style: italic;">여기에 감각적인 인용문을 입력하세요.</blockquote>' },
  { name: '아이콘 포인트', html: '<blockquote style="display: flex; gap: 15px; background: #f1f5f9; padding: 20px; border-radius: 16px; margin: 20px 0; color: #334155;"><span style="font-size: 24px;">💡</span><div><p style="margin: 0; font-weight: 800;">Tip & Quote</p><p style="margin: 5px 0 0 0; opacity: 0.8;">여기에 유용한 팁이나 인용구를 입력하세요.</p></div></blockquote>' }
];

const DIVIDERS = [
  { name: '가는 실선', html: '<hr style="border: 0; height: 1px; background: #e2e8f0; margin: 40px 0;" />' },
  { name: '점선 구분', html: '<hr style="border: 0; border-top: 2px dashed #cbd5e1; margin: 40px 0;" />' },
  { name: '도트 포인트', html: '<div style="display: flex; align-items: center; justify-content: center; gap: 10px; margin: 40px 0;"><div style="flex: 1; height: 1px; background: #f1f5f9;"></div><div style="width: 8px; height: 8px; background: #6366f1; border-radius: 50%;"></div><div style="flex: 1; height: 1px; background: #f1f5f9;"></div></div>' },
  { name: '그라디언트', html: '<hr style="border: 0; height: 2px; background-image: linear-gradient(to right, transparent, #6366f1, transparent); margin: 40px 0;" />' },
  { name: '이중선', html: '<hr style="border: 0; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; height: 4px; margin: 40px 0;" />' }
];

const CARRIERS = [
  "CJ대한통운",
  "로젠택배",
  "우체국택배",
  "한진택배",
  "롯데택배",
  "경동택배"
];

const getTrackingUrl = (company: string, number: string) => {
  if (!number) return null;
  switch (company) {
    case 'CJ대한통운': return `https://www.doortodoor.co.kr/parcel/doortodoor.do?f_invc_no=${number}`;
    case '로젠택배': return `https://www.ilogen.com/web/personal/trace/${number}`;
    case '우체국택배': return `https://service.epost.go.kr/trace.RetrieveDomakskTraceList.comm?sid1=${number}`;
    case '한진택배': return `https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mCode=MN038&wblNum=${number}`;
    case '롯데택배': return `https://www.lotteglogis.com/home/reservation/tracking/indexByWaybill?invcNo=${number}`;
    default: return null;
  }
};

const WebEditor: React.FC<{ value: string; onChange: (html: string) => void }> = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const selectionRange = useRef<Range | null>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value, activeTab]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current && editorRef.current.contains(sel.anchorNode)) {
      selectionRange.current = sel.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (selectionRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(selectionRange.current);
    }
  };

  const exec = (cmd: string, arg?: string) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(cmd, false, arg);
    onChange(editorRef.current?.innerHTML || '');
    setActiveDropdown(null);
  };

  const insertLink = async () => {
    saveSelection();
    const url = await window.customPrompt('연결할 URL을 입력하세요:', 'https://');
    restoreSelection();
    if (url) exec('createLink', url);
  };

  const insertImageByUrl = async () => {
    saveSelection();
    const url = await window.customPrompt('이미지 주소를 입력하세요:', 'https://');
    restoreSelection();
    if (url) exec('insertImage', url);
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        restoreSelection();
        const base64 = event.target?.result as string;
        exec('insertHTML', `<img src="${base64}" style="max-width: 100%; border-radius: 16px; margin: 10px auto; display: block;" /><p><br/></p>`);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const insertVideoByUrl = async () => {
    saveSelection();
    const url = await window.customPrompt('동영상 주소(유튜브 등)를 입력하세요:', 'https://');
    restoreSelection();
    
    if (url) {
      let videoId = '';
      const match = url.match(/(?:youtu\.be\/|(?:www\.|m\.|music\.)?youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
      
      if (match && match[1]) {
        videoId = match[1];
      }

      if (videoId) {
        const origin = window.location.origin;
        const src = `https://www.youtube.com/embed/${videoId}?origin=${encodeURIComponent(origin)}&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
        const embedHtml = `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:16px;margin:20px auto;max-width:100%;"><iframe src="${src}" title="YouTube video player" style="position:absolute;top:0;left:0;width:100%;height:100%;" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div><p><br/></p>`;
        exec('insertHTML', embedHtml);
      } else {
        const embedHtml = `<video src="${url}" controls style="max-width: 100%; border-radius: 16px; margin: 20px auto; display: block;"></video><p><br/></p>`;
        exec('insertHTML', embedHtml);
      }
    }
  };

  const handleVideoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        restoreSelection();
        const base64 = event.target?.result as string;
        exec('insertHTML', `<video src="${base64}" controls style="max-width: 100%; border-radius: 16px; margin: 20px auto; display: block;"></video><p><br/></p>`);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const insertEmoji = (emoji: string) => {
    exec('insertHTML', emoji);
  };

  const insertTemplate = (html: string) => {
    exec('insertHTML', html);
  };

  const toggleDropdown = (name: string) => {
    setActiveDropdown(prev => prev === name ? null : name);
  };

  const preventFocusLoss = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  return (
    <div ref={containerRef} className="border-2 border-gray-100 rounded-[32px] bg-white shadow-2xl transition-all duration-300 relative">
      <div className="flex bg-gray-100 p-1 rounded-t-[30px]">
        <button onClick={() => setActiveTab('edit')} className={`flex-1 py-3 text-xs font-black rounded-t-2xl transition-all ${activeTab === 'edit' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>편집 모드</button>
        <button onClick={() => setActiveTab('preview')} className={`flex-1 py-3 text-xs font-black rounded-t-2xl transition-all ${activeTab === 'preview' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>미리보기</button>
      </div>

      {activeTab === 'edit' ? (
        <>
          <div className="bg-gray-50/95 p-2 md:p-3 border-b border-gray-100 flex flex-wrap gap-2 items-center sticky top-16 z-[40] backdrop-blur-md">
            <div className="flex gap-1 items-center bg-white p-1 rounded-xl shadow-sm shrink-0">
              <select onChange={(e) => exec('fontSize', e.target.value)} className="h-8 px-2 rounded-lg text-xs font-black border-none outline-none bg-transparent">
                <option value="3">본문</option>
                <option value="5">소제목</option>
                <option value="7">대제목</option>
              </select>
              <div className="w-px h-4 bg-gray-100 mx-1"></div>
              <input type="color" onChange={(e) => exec('foreColor', e.target.value)} className="w-6 h-6 border-none cursor-pointer bg-transparent" title="글자색" />
            </div>

            <div className="flex gap-1 bg-white p-1 rounded-xl shadow-sm shrink-0">
              <button onMouseDown={preventFocusLoss} onClick={() => exec('bold')} className="w-8 h-8 font-black hover:bg-gray-100 rounded-lg">B</button>
              <button onMouseDown={preventFocusLoss} onClick={() => exec('italic')} className="w-8 h-8 italic font-serif hover:bg-gray-100 rounded-lg">I</button>
              <button onMouseDown={preventFocusLoss} onClick={() => exec('underline')} className="w-8 h-8 underline hover:bg-gray-100 rounded-lg">U</button>
            </div>

            <div className="hidden md:flex gap-1 bg-white p-1 rounded-xl shadow-sm shrink-0">
              <button onMouseDown={preventFocusLoss} onClick={() => exec('justifyLeft')} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg" title="왼쪽 정렬">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h10M4 18h16"/></svg>
              </button>
              <button onMouseDown={preventFocusLoss} onClick={() => exec('justifyCenter')} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg" title="가운데 정렬">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M7 12h10M4 18h16"/></svg>
              </button>
              <button onMouseDown={preventFocusLoss} onClick={() => exec('justifyRight')} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg" title="오른쪽 정렬">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M4 6h16M10 12h10M4 18h16"/></svg>
              </button>
            </div>
            
            <div className="relative shrink-0">
              <button onMouseDown={preventFocusLoss} onClick={() => toggleDropdown('quote')} className={`h-9 px-3 bg-white rounded-xl shadow-sm text-xs font-black hover:bg-indigo-50 flex items-center gap-1 transition ${activeDropdown === 'quote' ? 'ring-2 ring-indigo-600' : ''}`}>
                <span>💬</span>
              </button>
              {activeDropdown === 'quote' && (
                <div className="absolute top-full left-0 mt-2 flex flex-col bg-white shadow-2xl rounded-2xl p-2 z-[60] border border-gray-100 min-w-[180px] animate-in slide-in-from-top-2 duration-200">
                  {BLOCKQUOTES.map((q, idx) => (
                    <button key={idx} onMouseDown={preventFocusLoss} onClick={() => insertTemplate(q.html)} className="p-2.5 text-xs font-black hover:bg-gray-50 text-left rounded-lg truncate transition">
                      {q.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative shrink-0">
              <button onMouseDown={preventFocusLoss} onClick={() => toggleDropdown('divider')} className={`h-9 px-3 bg-white rounded-xl shadow-sm text-xs font-black hover:bg-indigo-50 flex items-center gap-1 transition ${activeDropdown === 'divider' ? 'ring-2 ring-indigo-600' : ''}`}>
                <span>➖</span>
              </button>
              {activeDropdown === 'divider' && (
                <div className="absolute top-full left-0 mt-2 flex flex-col bg-white shadow-2xl rounded-2xl p-2 z-[60] border border-gray-100 min-w-[180px] animate-in slide-in-from-top-2 duration-200">
                  {DIVIDERS.map((d, idx) => (
                    <button key={idx} onMouseDown={preventFocusLoss} onClick={() => insertTemplate(d.html)} className="p-2.5 text-xs font-black hover:bg-gray-50 text-left rounded-lg truncate transition">
                      {d.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative shrink-0">
              <button onMouseDown={preventFocusLoss} onClick={() => toggleDropdown('emoji')} className={`h-9 px-3 bg-white rounded-xl shadow-sm text-xs font-black hover:bg-indigo-50 flex items-center gap-1 transition ${activeDropdown === 'emoji' ? 'ring-2 ring-indigo-600' : ''}`}>
                <span>😊</span>
              </button>
              {activeDropdown === 'emoji' && (
                <div className="absolute top-full left-0 mt-2 grid grid-cols-8 gap-1 bg-white shadow-2xl rounded-2xl p-3 z-[60] border border-gray-100 min-w-[280px] animate-in slide-in-from-top-2 duration-200">
                  {EMOJIS.map((emoji, i) => (
                    <button key={i} onMouseDown={preventFocusLoss} onClick={() => insertEmoji(emoji)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg text-lg transition-transform active:scale-125">{emoji}</button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative shrink-0">
              <button onMouseDown={preventFocusLoss} onClick={() => toggleDropdown('media')} className={`h-9 px-3 bg-white rounded-xl shadow-sm text-xs font-black hover:bg-indigo-50 flex items-center gap-1 transition ${activeDropdown === 'media' ? 'ring-2 ring-indigo-600' : ''}`}>
                <span>📸</span>
              </button>
              {activeDropdown === 'media' && (
                <div className="absolute top-full left-0 mt-2 flex flex-col bg-white shadow-2xl rounded-2xl p-2 z-[60] border border-gray-100 min-w-[160px] animate-in slide-in-from-top-2 duration-200">
                  <button onMouseDown={preventFocusLoss} onClick={insertImageByUrl} className="p-2.5 text-xs font-black hover:bg-gray-50 text-left rounded-lg transition">🔗 URL 이미지</button>
                  <button onMouseDown={preventFocusLoss} onClick={() => { saveSelection(); fileInputRef.current?.click(); setActiveDropdown(null); }} className="p-2.5 text-xs font-black hover:bg-gray-50 text-left rounded-lg transition">📁 로컬 이미지</button>
                  <button onMouseDown={preventFocusLoss} onClick={insertVideoByUrl} className="p-2.5 text-xs font-black hover:bg-gray-50 text-left rounded-lg transition">🎬 URL 동영상</button>
                  <button onMouseDown={preventFocusLoss} onClick={() => { saveSelection(); videoInputRef.current?.click(); setActiveDropdown(null); }} className="p-2.5 text-xs font-black hover:bg-gray-50 text-left rounded-lg transition">📂 로컬 동영상</button>
                </div>
              )}
            </div>

            <div className="flex gap-1 bg-white p-1 rounded-xl shadow-sm shrink-0">
              <button onMouseDown={preventFocusLoss} onClick={insertLink} className="p-2 hover:bg-indigo-50 rounded-lg text-indigo-600 transition" title="링크 삽입">🔗</button>
            </div>
          </div>
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageFile} />
          <input type="file" ref={videoInputRef} className="hidden" accept="video/*" onChange={handleVideoFile} />
          <div 
            ref={editorRef}
            contentEditable
            onInput={() => onChange(editorRef.current?.innerHTML || '')}
            onBlur={saveSelection}
            className="p-4 md:p-10 min-h-[500px] outline-none prose prose-indigo max-w-none text-base leading-relaxed text-center prose-img:mx-auto prose-video:mx-auto relative z-0 rounded-b-[30px]"
          />
        </>
      ) : (
        <div className="p-4 md:p-10 min-h-[500px] prose prose-indigo max-w-none bg-gray-50/30 overflow-y-auto text-center prose-img:mx-auto prose-video:mx-auto rounded-b-[30px]" dangerouslySetInnerHTML={{ __html: value || '<p class="text-center text-gray-300 italic py-20">작성된 내용이 없습니다.</p>' }} />
      )}
    </div>
  );
};

const AdminDashboard: React.FC<AdminDashboardProps> = ({ 
  products, categories, onUpdateCategories, onAddProduct, onUpdateProduct, onDeleteProduct, users, orders, onUpdateUserPoints, onUpdateUser, onDeleteUser, onUpdateOrderStatus 
}) => {
  const [activeMainTab, setActiveMainTab] = useState<'stats' | 'orders' | 'products' | 'users' | 'categories' | 'notices'>('stats');
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);
  const [editingOrder, setEditingOrder] = useState<Partial<Order> | null>(null);
  const [editingMember, setEditingMember] = useState<User | null>(null); 
  const [editingNotice, setEditingNotice] = useState<Partial<Notice> | null>(null);
  const [allNotices, setAllNotices] = useState<Notice[]>([]);
  const [orderFilter, setOrderFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const [trackingForm, setTrackingForm] = useState<{ id: string, company: string, number: string } | null>(null);
  const [newCatInput, setNewCatInput] = useState('');
  const [statsPeriod, setStatsPeriod] = useState<'today' | '7days' | '30days' | 'monthly' | 'yearly' | 'total'>('total');
  const [targetYear, setTargetYear] = useState(new Date().getFullYear());
  const [targetMonth, setTargetMonth] = useState(new Date().getMonth() + 1);
  const excelInputRef = useRef<HTMLInputElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const [visibleOrdersCount, setVisibleOrdersCount] = useState(10);
  const [visibleProductsCount, setVisibleProductsCount] = useState(50);
  
  const [productSearchQuery, setProductSearchQuery] = useState('');

  const [newReviews, setNewReviews] = useState<Review[]>([]);
  const [newQNAs, setNewQNAs] = useState<QNAItem[]>([]);

  useEffect(() => {
    const qReviews = query(collection(db, "reviews"), where("status", "==", "답변대기"));
    const unsubReviews = onSnapshot(qReviews, (snap) => {
      setNewReviews(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Review)));
    });

    const qQNAs = query(collection(db, "qnas"), where("status", "==", "답변대기"));
    const unsubQNAs = onSnapshot(qQNAs, (snap) => {
      setNewQNAs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as QNAItem)));
    });

    const qNotices = query(collection(db, "notices"), orderBy("createdAt", "desc"));
    const unsubNotices = onSnapshot(qNotices, (snap) => {
      setAllNotices(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Notice)));
    });

    return () => {
      unsubReviews();
      unsubQNAs();
      unsubNotices();
    };
  }, []);

  useEffect(() => {
    setVisibleOrdersCount(10);
    setVisibleProductsCount(50);
  }, [activeMainTab, orderFilter]);

  useEffect(() => {
    if (!editingProduct && activeMainTab === 'products') {
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [editingProduct, activeMainTab]);

  const getProcessedProducts = () => {
    let processed = [...products];

    if (productSearchQuery.trim()) {
      const q = productSearchQuery.toLowerCase();
      processed = processed.filter(p => 
        p.name.toLowerCase().includes(q) || 
        p.id.toLowerCase().includes(q)
      );
    }

    processed.sort((a, b) => {
      if (a.id < b.id) return 1;
      if (a.id > b.id) return -1;
      return 0;
    });

    return processed;
  };

  const processedProducts = getProcessedProducts();
  const displayedProducts = processedProducts.slice(0, visibleProductsCount);

  const handleStartEditProduct = (p: Partial<Product> | null) => {
    scrollPositionRef.current = window.scrollY;
    setEditingProduct(p);
  };

  const handleSaveNotice = async () => {
    if (!editingNotice?.title || !editingNotice?.content) {
      if(window.customAlert) await window.customAlert('제목과 내용을 입력해주세요.');
      return;
    }
    try {
      if (editingNotice.id) {
        await updateDoc(doc(db, "notices", editingNotice.id), {
          title: editingNotice.title,
          content: editingNotice.content,
          image: editingNotice.image || '',
          isImportant: !!editingNotice.isImportant
        });
        if(window.customAlert) await window.customAlert('공지사항이 수정되었습니다.');
      } else {
        await addDoc(collection(db, "notices"), {
          title: editingNotice.title,
          content: editingNotice.content,
          image: editingNotice.image || '',
          isImportant: !!editingNotice.isImportant,
          createdAt: new Date().toISOString()
        });
        if(window.customAlert) await window.customAlert('새 공지사항이 등록되었습니다.');
      }
      setEditingNotice(null);
    } catch (e) {
      console.error(e);
      if(window.customAlert) await window.customAlert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteNotice = async (id: string) => {
    if (!(await window.customConfirm('정말 삭제하시겠습니까?'))) return;
    try {
      await deleteDoc(doc(db, "notices", id));
      if(window.customAlert) await window.customAlert('삭제되었습니다.');
    } catch (e) {
      console.error(e);
    }
  };

  const handleFullDeleteProduct = async (productId: string) => {
    if(!(await window.customConfirm('정말 이 상품을 삭제하시겠습니까? 관련 리뷰와 Q&A도 모두 삭제됩니다.'))) return;

    try {
      const qReviews = query(collection(db, "reviews"), where("productId", "==", productId));
      const reviewSnaps = await getDocs(qReviews);
      for (const d of reviewSnaps.docs) {
        await deleteDoc(doc(db, "reviews", d.id));
      }

      const qQnas = query(collection(db, "qnas"), where("productId", "==", productId));
      const qnaSnaps = await getDocs(qQnas);
      for (const d of qnaSnaps.docs) {
        await deleteDoc(doc(db, "qnas", d.id));
      }

      onDeleteProduct(productId);
      if(window.customAlert) await window.customAlert('상품과 관련된 모든 피드백 데이터가 정상적으로 삭제되었습니다.');
    } catch (e) {
      console.error("Cascading delete failed:", e);
      if(window.customAlert) await window.customAlert('삭제 처리 중 오류가 발생했습니다.');
    }
  };

  const getFilteredStats = () => {
    const now = new Date();
    const filterByDate = (dateStr: string) => {
      const date = new Date(dateStr);
      if (statsPeriod === 'today') return dateStr.split('T')[0] === now.toISOString().split('T')[0];
      if (statsPeriod === '7days') return (now.getTime() - date.getTime()) <= 7 * 24 * 60 * 60 * 1000;
      if (statsPeriod === '30days') return (now.getTime() - date.getTime()) <= 30 * 24 * 60 * 60 * 1000;
      if (statsPeriod === 'monthly') return date.getFullYear() === targetYear && (date.getMonth() + 1) === targetMonth;
      if (statsPeriod === 'yearly') return date.getFullYear() === targetYear;
      return true;
    };

    const filteredOrders = orders.filter(o => filterByDate(o.createdAt));
    const filteredUsers = users.filter(u => filterByDate(u.joinedAt));
    
    return {
      sales: filteredOrders.reduce((acc, o) => acc + (o.status !== 'CANCELED' ? o.totalAmount : 0), 0),
      orderCount: filteredOrders.length,
      userCount: filteredUsers.length,
      lowStock: products.filter(p => p.stock < 10)
    };
  };

  const periodStats = getFilteredStats();

  const handleExportCSV = () => {
    const headers = ["ID", "Name", "Categories", "Price", "OriginalPrice", "Stock", "ShippingFee", "IsNew", "Image", "Description", "OptionsJSON", "AddOnsJSON"];
    const rows = products.map(p => [
      p.id,
      `"${p.name.replace(/"/g, '""')}"`,
      `"${p.categories.join(',')}"`,
      p.price,
      p.originalPrice || 0,
      p.stock,
      p.shippingFee,
      p.isNew ? "TRUE" : "FALSE",
      `"${p.image}"`,
      `"${(p.description || '').replace(/"/g, '""')}"`,
      `"${JSON.stringify(p.options || []).replace(/"/g, '""')}"`,
      `"${JSON.stringify(p.addOns || []).replace(/"/g, '""')}"`
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `makerstory_products_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split("\n").filter(l => l.trim() !== "");
      if (lines.length < 2) return;

      const items: Product[] = [];
      const headers = lines[0].split(",").map(h => h.trim().replace(/^\uFEFF/, ''));

      for (let i = 1; i < lines.length; i++) {
        const row: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let char of lines[i]) {
            if (char === '"') inQuotes = !inQuotes;
            else if (char === ',' && !inQuotes) {
                row.push(current.trim());
                current = '';
            } else current += char;
        }
        row.push(current.trim());

        const getVal = (header: string) => {
          const idx = headers.indexOf(header);
          if (idx === -1) return "";
          let val = row[idx] || "";
          if (val.startsWith('"') && val.endsWith('"')) val = val.substring(1, val.length - 1);
          return val.replace(/""/g, '"');
        };

        try {
          const cleanNum = (val: any) => {
            const n = Number(val);
            return isNaN(n) ? 0 : n;
          };

          const prod: Product = {
            id: getVal("ID") || `prod-${Date.now()}-${i}`,
            name: getVal("Name") || "이름 없는 상품",
            categories: getVal("Categories").split(",").filter(c => c !== ""),
            price: cleanNum(getVal("Price")),
            originalPrice: cleanNum(getVal("OriginalPrice")) || undefined,
            stock: cleanNum(getVal("Stock")),
            shippingFee: cleanNum(getVal("ShippingFee")),
            isNew: getVal("IsNew").toUpperCase() === "TRUE",
            image: getVal("Image") || "https://placehold.co/400x400?text=No+Image",
            description: getVal("Description") || "",
            options: JSON.parse(getVal("OptionsJSON") || "[]"),
            addOns: JSON.parse(getVal("AddOnsJSON") || "[]"),
          };
          items.push(prod);
        } catch (err) {
          console.error("CSV Parse Error at line", i, err);
        }
      }

      if (items.length > 0) {
        if (await window.customConfirm(`${items.length}개의 상품을 일괄 등록/수정하시겠습니까?`)) {
          try {
            for (const item of items) {
              await onUpdateProduct(item);
            }
            if(window.customAlert) await window.customAlert('일괄 등록이 완료되었습니다.');
          } catch (dbErr: any) {
            console.error("Batch DB Error:", dbErr);
            if(window.customAlert) await window.customAlert(`일괄 등록 중 오류가 발생했습니다: ${dbErr.message}`);
          }
        }
      }
      if (excelInputRef.current) excelInputRef.current.value = "";
    };
    reader.readAsText(file);
  };

  const handleSaveOrder = async () => {
    if (!editingOrder) return;
    if (editingOrder.id) {
       onUpdateOrderStatus(editingOrder.id, editingOrder.status || 'PAYMENT_COMPLETE', {
         number: editingOrder.trackingNumber || '',
         company: editingOrder.trackingCompany || ''
       });
       if(window.customAlert) await window.customAlert('주문 정보가 성공적으로 업데이트되었습니다.');
    }
    setEditingOrder(null);
  };

  const handleSaveMember = async () => {
    if (!editingMember) return;
    onUpdateUser(editingMember);
    if(window.customAlert) await window.customAlert('회원 정보가 성공적으로 업데이트되었습니다.');
    setEditingMember(null);
  };

  const handleApplyTracking = async (orderId: string) => {
    if (!trackingForm || !trackingForm.number || !trackingForm.company) {
      if(window.customAlert) await window.customAlert('택배사와 운송장 번호를 모두 입력해주세요.');
      return;
    }
    onUpdateOrderStatus(orderId, 'SHIPPING', { company: trackingForm.company, number: trackingForm.number });
    setTrackingForm(null);
  };

  const addOption = () => {
    const newOptions = [...(editingProduct?.options || []), { id: `opt-${Date.now()}`, name: '', values: [] }];
    setEditingProduct(prev => prev ? ({ ...prev, options: newOptions }) : null);
  };
  const removeOption = (idx: number) => {
    const newOptions = [...(editingProduct?.options || [])];
    newOptions.splice(idx, 1);
    setEditingProduct(prev => prev ? ({ ...prev, options: newOptions }) : null);
  };
  const addOptionValue = (optIdx: number) => {
    const newOptions = [...(editingProduct?.options || [])];
    newOptions[optIdx].values.push({ name: '', extraPrice: 0 });
    setEditingProduct(prev => prev ? ({ ...prev, options: newOptions }) : null);
  };
  const removeOptionValue = (optIdx: number, valIdx: number) => {
    const newOptions = [...(editingProduct?.options || [])];
    newOptions[optIdx].values.splice(valIdx, 1);
    setEditingProduct(prev => prev ? ({ ...prev, options: newOptions }) : null);
  };
  const addAddon = () => {
    const newAddons = [...(editingProduct?.addOns || []), { id: `add-${Date.now()}`, name: '', price: 0 }];
    setEditingProduct(prev => prev ? ({ ...prev, addOns: newAddons }) : null);
  };
  const removeAddon = (idx: number) => {
    const newAddons = [...(editingProduct?.addOns || [])];
    newAddons.splice(idx, 1);
    setEditingProduct(prev => prev ? ({ ...prev, addOns: newAddons }) : null);
  };

  const handleSaveProduct = async () => {
    if (!editingProduct?.name || !editingProduct?.price) {
      if(window.customAlert) await window.customAlert('상품명과 가격을 입력해주세요.');
      return;
    }
    
    const nextId = editingProduct.id || `prod-${Date.now()}`;
    const cleanNum = (val: any) => {
      const n = Number(val);
      return isNaN(n) ? 0 : n;
    };

    const finalProduct: Product = {
      id: nextId,
      name: editingProduct.name || '',
      categories: editingProduct.categories || [],
      price: cleanNum(editingProduct.price),
      originalPrice: cleanNum(editingProduct.originalPrice),
      image: editingProduct.image || 'https://placehold.co/400x400?text=No+Image',
      description: editingProduct.description || '',
      isNew: !!editingProduct.isNew,
      stock: cleanNum(editingProduct.stock),
      shippingFee: cleanNum(editingProduct.shippingFee),
      options: (editingProduct.options || []).map(opt => ({
        id: opt.id || `opt-${Math.random()}`,
        name: opt.name || '',
        values: (opt.values || []).map(v => ({
          name: v.name || '',
          extraPrice: cleanNum(v.extraPrice)
        }))
      })),
      addOns: (editingProduct.addOns || []).map(add => ({
        id: add.id || `add-${Math.random()}`,
        name: add.name || '',
        price: cleanNum(add.price)
      }))
    };

    const normalizedData = JSON.parse(JSON.stringify(finalProduct));

    try {
      if (editingProduct.id) {
        await onUpdateProduct(normalizedData);
        if(window.customAlert) await window.customAlert('상품 정보가 수정되었습니다.');
      } else {
        await onAddProduct(normalizedData);
        if(window.customAlert) await window.customAlert('새 상품이 성공적으로 등록되었습니다.');
      }
      setEditingProduct(null);
    } catch (err: any) {
      console.error("Save Product Fail:", err);
      if(window.customAlert) await window.customAlert(`상품 저장 중 오류가 발생했습니다: ${err.message || '알 수 없는 오류'}`);
    }
  };

  const filteredOrders = orderFilter === 'ALL' ? orders : orders.filter(o => o.status === orderFilter);
  const displayedOrders = filteredOrders.slice(0, visibleOrdersCount);
  
  const getBestSellers = () => {
    const now = new Date();
    const filterByDate = (dateStr: string) => {
      const date = new Date(dateStr);
      if (statsPeriod === 'today') return dateStr.split('T')[0] === now.toISOString().split('T')[0];
      if (statsPeriod === '7days') return (now.getTime() - date.getTime()) <= 7 * 24 * 60 * 60 * 1000;
      if (statsPeriod === '30days') return (now.getTime() - date.getTime()) <= 30 * 24 * 60 * 60 * 1000;
      if (statsPeriod === 'monthly') return date.getFullYear() === targetYear && (date.getMonth() + 1) === targetMonth;
      if (statsPeriod === 'yearly') return date.getFullYear() === targetYear;
      return true;
    };

    const itemCounts: Record<string, { count: number, name: string, image: string }> = {};
    orders.forEach(o => {
      if (o.status !== 'CANCELED' && filterByDate(o.createdAt)) {
        o.items.forEach(item => {
          if (!itemCounts[item.id]) {
            itemCounts[item.id] = { count: 0, name: item.name, image: item.image };
          }
          itemCounts[item.id].count += item.quantity;
        });
      }
    });
    return Object.values(itemCounts).sort((a, b) => b.count - a.count).slice(0, 5);
  };

  const bestSellers = getBestSellers();

  return (
    <div className="container mx-auto px-4 py-6 md:py-12 min-h-screen">
      <div className="flex flex-col lg:flex-row justify-between items-center mb-10 md:mb-16 gap-6 md:gap-8">
        <div className="flex items-center gap-4 md:gap-5 w-full md:w-auto">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-900 rounded-2xl md:rounded-3xl flex items-center justify-center text-white font-black text-xl md:text-3xl shadow-2xl shrink-0">M</div>
          <div>
            <h1 className="text-2xl md:text-4xl font-black italic tracking-tighter uppercase leading-none">Management</h1>
            <p className="text-[10px] md:text-sm font-bold text-indigo-600 mt-1 md:mt-2 uppercase tracking-widest">Maker Story Admin Suite</p>
          </div>
        </div>
        <div className="flex bg-gray-100 p-1.5 md:p-2 rounded-2xl md:rounded-[32px] gap-1 md:gap-2 shadow-inner overflow-x-auto max-w-full scrollbar-hide">
          {[
            {id: 'stats', label: '대시보드'}, 
            {id: 'orders', label: '주문/배송'}, 
            {id: 'products', label: '상품관리'}, 
            {id: 'categories', label: '카테고리관리'}, 
            {id: 'notices', label: '공지사항관리'},
            {id: 'users', label: '회원관리'}
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveMainTab(tab.id as any)} 
              className={`px-5 md:px-8 py-3 md:py-4 rounded-xl md:rounded-[24px] text-xs md:text-sm font-black transition-all whitespace-nowrap ${activeMainTab === tab.id ? 'bg-white text-indigo-600 shadow-xl' : 'text-gray-400 hover:text-gray-900'}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeMainTab === 'notices' && (
        <div className="space-y-8 animate-in slide-in-from-bottom-8">
          {editingNotice ? (
            <div className="bg-white rounded-3xl md:rounded-[56px] p-6 md:p-12 shadow-2xl border border-gray-100 animate-in zoom-in-95">
              <div className="flex justify-between items-center mb-10 pb-6 border-b border-gray-100">
                <h3 className="text-xl md:text-3xl font-black italic tracking-tighter uppercase">Notice Editor</h3>
                <button onClick={() => setEditingNotice(null)} className="p-2 hover:bg-gray-100 rounded-full transition"><svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12" strokeWidth={3}/></svg></button>
              </div>
              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">제목</label>
                  <input type="text" value={editingNotice.title || ''} onChange={e => setEditingNotice({...editingNotice, title: e.target.value})} className="w-full p-4 bg-gray-50 border-none rounded-2xl outline-none font-black shadow-inner" placeholder="공지사항 제목을 입력하세요" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">대표 이미지 URL</label>
                  <input type="text" value={editingNotice.image || ''} onChange={e => setEditingNotice({...editingNotice, image: e.target.value})} className="w-full p-4 bg-gray-50 border-none rounded-2xl outline-none font-bold text-xs shadow-inner" placeholder="대표 이미지 URL을 입력하세요" />
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <input type="checkbox" id="important" checked={!!editingNotice.isImportant} onChange={e => setEditingNotice({...editingNotice, isImportant: e.target.checked})} className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  <label htmlFor="important" className="text-sm font-bold text-gray-600">중요 공지로 표시 ( 사용자 페이지에서 Urgent 배지 노출 )</label>
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 block mb-4 uppercase">상세 내용</label>
                  <WebEditor value={editingNotice.content || ''} onChange={html => setEditingNotice({...editingNotice, content: html})} />
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-10 pt-6 border-t border-gray-100">
                <button onClick={() => setEditingNotice(null)} className="px-8 py-3.5 bg-gray-100 rounded-xl font-black text-xs text-gray-500">취소</button>
                <button onClick={handleSaveNotice} className="px-12 py-3.5 bg-indigo-600 text-white rounded-xl font-black text-xs shadow-xl">공지 저장</button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl md:rounded-[56px] border border-gray-100 overflow-hidden shadow-2xl">
              <div className="p-6 md:p-10 border-b border-gray-50 bg-gray-50/30 flex justify-between items-center">
                 <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tighter">Notice List</h2>
                 <button onClick={() => setEditingNotice({ title: '', content: '', image: '', isImportant: false })} className="px-6 py-3 bg-gray-900 text-white rounded-xl text-xs font-black shadow-xl hover:bg-indigo-600 transition">새 공지 등록</button>
              </div>
              
              {/* Mobile View (Card List) */}
              <div className="block md:hidden bg-gray-50 p-4 space-y-4">
                {allNotices.map(n => (
                  <div key={n.id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex gap-4 mb-3">
                       {n.image && <img src={n.image} className="w-16 h-16 rounded-lg object-cover" />}
                       <div>
                          {n.isImportant && <span className="px-2 py-0.5 bg-red-50 text-red-500 text-[10px] font-black rounded uppercase mb-1 inline-block">Urgent</span>}
                          <h4 className="font-black text-gray-900 text-sm line-clamp-2">{n.title}</h4>
                          <p className="text-[10px] text-gray-400 mt-1">{new Date(n.createdAt).toLocaleDateString()}</p>
                       </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-50">
                       <button onClick={() => setEditingNotice(n)} className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-black">수정</button>
                       <button onClick={() => handleDeleteNotice(n.id)} className="px-4 py-2 bg-red-50 text-red-500 rounded-xl text-xs font-black">삭제</button>
                    </div>
                  </div>
                ))}
                {allNotices.length === 0 && <div className="p-10 text-center text-gray-300 italic">등록된 공지사항이 없습니다.</div>}
              </div>

              {/* Desktop View (Table) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left">
                  <tbody className="divide-y divide-gray-50">
                    {allNotices.map(n => (
                      <tr key={n.id} className="hover:bg-gray-50/20 transition group">
                        <td className="px-10 py-6">
                           <div className="flex items-center gap-4">
                              {n.image ? (
                                <img src={n.image} className="w-12 h-12 rounded-lg object-cover border border-gray-100" alt="" />
                              ) : (
                                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] text-gray-400">No Img</div>
                              )}
                              <div>
                                <div className="flex items-center gap-3">
                                   {n.isImportant && <span className="px-2 py-0.5 bg-red-50 text-red-500 text-[8px] font-black rounded uppercase">Urgent</span>}
                                   <p className="font-black text-gray-900">{n.title}</p>
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1">{new Date(n.createdAt).toLocaleString()}</p>
                              </div>
                           </div>
                        </td>
                        <td className="px-10 py-6 text-right">
                          <div className="flex gap-2 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setEditingNotice(n)} className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg></button>
                            <button onClick={() => handleDeleteNotice(n.id)} className="p-2 bg-red-50 text-red-500 rounded-lg"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {allNotices.length === 0 && <tr><td className="px-10 py-20 text-center text-gray-300 italic">등록된 공지사항이 없습니다.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeMainTab === 'stats' && (
        <div className="animate-in fade-in duration-700 space-y-6 md:space-y-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end bg-white p-6 md:p-10 rounded-3xl md:rounded-[48px] shadow-sm border border-gray-50 gap-6">
             <div>
                <h2 className="text-xl md:text-2xl font-black italic tracking-tighter uppercase mb-1 md:mb-2">Operation Summary</h2>
                <p className="text-[10px] md:text-sm font-bold text-gray-400">데이터 기반 쇼핑몰 분석 리포트</p>
             </div>
             <div className="flex flex-col items-start md:items-end gap-3 w-full md:w-auto">
               <div className="flex bg-gray-100 p-1 rounded-xl md:rounded-2xl gap-1 overflow-x-auto max-w-full scrollbar-hide">
                  {[
                    { id: 'today', label: '오늘' },
                    { id: '7days', label: '7일' },
                    { id: '30days', label: '30일' },
                    { id: 'monthly', label: '월별' },
                    { id: 'yearly', label: '연도별' },
                    { id: 'total', label: '전체' }
                  ].map(p => (
                    <button key={p.id} onClick={() => setStatsPeriod(p.id as any)} 
                      className={`px-3 md:px-5 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black transition-all whitespace-nowrap ${statsPeriod === p.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-900'}`}>
                      {p.label}
                    </button>
                  ))}
               </div>
               
               {(statsPeriod === 'monthly' || statsPeriod === 'yearly') && (
                 <div className="flex gap-2 animate-in slide-in-from-top-2 duration-300">
                    <select 
                      value={targetYear} 
                      onChange={e => setTargetYear(Number(e.target.value))}
                      className="px-3 md:px-4 py-1.5 md:py-2 bg-white border border-gray-100 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black outline-none focus:ring-2 focus:ring-indigo-600 shadow-sm"
                    >
                      {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}년</option>)}
                    </select>
                    {statsPeriod === 'monthly' && (
                      <select 
                        value={targetMonth} 
                        onChange={e => setTargetMonth(Number(e.target.value))}
                        className="px-3 md:px-4 py-1.5 md:py-2 bg-white border border-gray-100 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black outline-none focus:ring-2 focus:ring-indigo-600 shadow-sm"
                      >
                        {Array.from({length: 12}, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}월</option>)}
                      </select>
                    )}
                 </div>
               )}
             </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {[
              { label: '매출액', value: `${periodStats.sales.toLocaleString()}원`, icon: '💰', color: 'text-indigo-600', trend: statsPeriod === 'monthly' ? `${targetYear}년 ${targetMonth}월` : statsPeriod === 'yearly' ? `${targetYear}년 전체` : '최종 결제 금액' },
              { label: '주문건수', value: `${periodStats.orderCount}건`, icon: '📦', color: 'text-blue-600', trend: '기간 내 모든 주문' },
              { label: '신규 가입자', value: `${periodStats.userCount}명`, icon: '👤', color: 'text-emerald-600', trend: '기간 내 신규 회원' }
            ].map((s, i) => (
              <div key={i} className="bg-white p-6 md:p-12 rounded-[32px] md:rounded-[56px] shadow-sm border border-gray-50 group hover:shadow-2xl transition-all duration-500">
                <div className="flex justify-between items-start mb-4 md:mb-6">
                  <span className="text-3xl md:text-5xl group-hover:scale-110 transition-transform duration-500">{s.icon}</span>
                  <div className="text-right">
                     <p className="text-[8px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5 md:mb-1">{s.label} Indicator</p>
                     <p className="text-[7px] md:text-[9px] font-bold text-indigo-400 italic">{s.trend}</p>
                  </div>
                </div>
                <p className="text-[10px] md:text-xs font-black text-gray-400 mb-1 md:mb-2 uppercase tracking-widest">{s.label}</p>
                <h3 className={`text-2xl md:text-5xl font-black tracking-tighter ${s.color} italic`}>{s.value}</h3>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10">
             <div className="bg-white p-6 md:p-12 rounded-[32px] md:rounded-[56px] shadow-sm border border-gray-50">
                <div className="flex justify-between items-center mb-6 md:mb-8">
                   <h3 className="text-lg md:text-xl font-black italic tracking-tighter uppercase">Low Stock Alert</h3>
                   <span className="px-3 md:px-4 py-1 bg-red-50 text-red-500 text-[8px] md:text-[10px] font-black rounded-full uppercase">Emergency</span>
                </div>
                <div className="space-y-3 md:space-y-4">
                   {periodStats.lowStock.length === 0 ? (
                     <div className="py-10 md:py-12 text-center bg-gray-50/50 rounded-2xl md:rounded-[32px]">
                        <p className="text-xs md:text-sm text-gray-300 font-bold italic">모든 상품의 재고가 충분합니다.</p>
                     </div>
                   ) : periodStats.lowStock.slice(0, 5).map(p => (
                     <div key={p.id} className="flex items-center justify-between p-3 md:p-4 bg-gray-50 rounded-2xl md:rounded-[28px] border border-transparent hover:border-red-100 transition">
                        <div className="flex items-center gap-3 md:gap-4">
                           <img src={p.image} className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl object-cover" />
                           <div>
                              <p className="text-xs md:text-sm font-black text-gray-800 line-clamp-1">{p.name}</p>
                              <p className="text-[8px] md:text-[10px] text-gray-400 font-bold">{p.categories.join(', ')}</p>
                           </div>
                        </div>
                        <div className="text-right">
                           <p className="text-[10px] md:text-xs font-black text-red-500 italic">현재 {p.stock}개</p>
                           <button onClick={async () => {
                             const newStock = await window.customPrompt(`${p.name}의 재고를수정합니다:`, String(p.stock));
                             if (newStock !== null) onUpdateProduct({...p, stock: Number(newStock)});
                           }} className="text-[8px] md:text-[9px] font-black text-indigo-600 underline">재고 충전</button>
                        </div>
                     </div>
                   ))}
                </div>
             </div>

             <div className="bg-white p-6 md:p-12 rounded-[32px] md:rounded-[56px] shadow-sm border border-gray-50">
                <div className="flex justify-between items-center mb-6 md:mb-8">
                   <h3 className="text-lg md:text-xl font-black italic tracking-tighter uppercase">Top Best Sellers</h3>
                   <span className="px-3 md:px-4 py-1 bg-indigo-50 text-indigo-500 text-[8px] md:text-[10px] font-black rounded-full uppercase">Performance</span>
                </div>
                <div className="space-y-3 md:space-y-4">
                   {bestSellers.length === 0 ? (
                     <div className="py-10 md:py-12 text-center bg-gray-50/50 rounded-2xl md:rounded-[32px]">
                        <p className="text-xs md:text-sm text-gray-300 font-bold italic">기간 내 판매 데이터가 없습니다.</p>
                     </div>
                   ) : bestSellers.map((item, idx) => (
                     <div key={idx} className="flex items-center justify-between p-3 md:p-4 bg-white border border-gray-50 rounded-2xl md:rounded-[28px] shadow-sm group hover:scale-[1.02] transition duration-300">
                        <div className="flex items-center gap-3 md:gap-4">
                           <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center bg-gray-900 text-white rounded-lg md:rounded-xl font-black italic text-[10px] md:text-xs shadow-lg">{idx + 1}</div>
                           <img src={item.image} className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl object-cover" />
                           <p className="text-xs md:text-sm font-black text-gray-800 line-clamp-1">{item.name}</p>
                        </div>
                        <div className="text-right">
                           <p className="text-[10px] md:text-xs font-black text-indigo-600 italic">{item.count}개</p>
                        </div>
                     </div>
                   ))}
                </div>
             </div>
          </div>

          <div className="bg-white p-6 md:p-12 rounded-3xl md:rounded-[56px] shadow-2xl border border-gray-100 animate-in slide-in-from-bottom-8">
             <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 md:mb-10 gap-4">
                <div>
                   <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tighter">Feedback Required</h2>
                   <p className="text-[10px] md:text-xs font-bold text-gray-400 mt-0.5">답변이 필요한 실시간 리뷰 및 Q&A</p>
                </div>
                <div className="flex gap-1.5 md:gap-2">
                   <span className="px-3 md:px-4 py-1 bg-indigo-600 text-white text-[8px] md:text-[10px] font-black rounded-full">리뷰 {newReviews.length}</span>
                   <span className="px-3 md:px-4 py-1 bg-gray-900 text-white text-[8px] md:text-[10px] font-black rounded-full">Q&A {newQNAs.length}</span>
                </div>
             </div>

             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                <div className="space-y-4">
                   <h3 className="text-xs md:text-sm font-black text-gray-900 uppercase italic mb-2 px-2">New Reviews</h3>
                   {newReviews.length === 0 ? (
                     <div className="py-12 md:py-20 text-center bg-gray-50 rounded-2xl md:rounded-[32px] border border-dashed border-gray-200">
                        <p className="text-gray-300 font-bold italic text-xs md:text-sm">리뷰 답변 완료</p>
                     </div>
                   ) : newReviews.map(r => (
                     <div key={r.id} className="p-4 md:p-6 bg-gray-50 rounded-2xl md:rounded-[32px] border border-transparent hover:border-indigo-100 transition group">
                        <div className="flex justify-between items-start mb-3">
                           <div>
                              <div className="text-amber-400 text-[10px]">{'★'.repeat(r.rating)}</div>
                              <p className="text-[10px] font-black text-gray-900 mt-1">{r.userName} <span className="text-gray-400 font-medium ml-1.5">{new Date(r.createdAt).toLocaleDateString()}</span></p>
                           </div>
                           <button 
                             onClick={() => window.open(`${window.location.origin}${window.location.pathname}#/product/${r.productId}`, '_blank')}
                             className="px-3 py-1.5 bg-indigo-600 text-white text-[8px] md:text-[10px] font-black rounded-lg hover:bg-gray-900 transition shadow-lg"
                           >
                             이동
                           </button>
                        </div>
                        <p className="text-[10px] md:text-xs text-gray-600 line-clamp-2 leading-relaxed">{r.content}</p>
                     </div>
                   ))}
                </div>

                <div className="space-y-4">
                   <h3 className="text-xs md:text-sm font-black text-gray-900 uppercase italic mb-2 px-2">New Q&A</h3>
                   {newQNAs.length === 0 ? (
                     <div className="py-12 md:py-20 text-center bg-gray-50 rounded-2xl md:rounded-[32px] border border-dashed border-gray-200">
                        <p className="text-gray-300 font-bold italic text-xs md:text-sm">문의 답변 완료</p>
                     </div>
                   ) : newQNAs.map(q => (
                     <div key={q.id} className="p-4 md:p-6 bg-gray-50 rounded-2xl md:rounded-[32px] border border-transparent hover:border-gray-200 transition group">
                        <div className="flex justify-between items-start mb-3">
                           <div>
                              <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-[7px] font-black rounded uppercase">Wait</span>
                              <p className="text-[10px] font-black text-gray-900 mt-1">{q.userName} <span className="text-gray-400 font-medium ml-1.5">{new Date(q.createdAt).toLocaleDateString()}</span></p>
                           </div>
                           <button 
                             onClick={() => window.open(`${window.location.origin}${window.location.pathname}#/product/${q.productId}`, '_blank')}
                             className="px-3 py-1.5 bg-gray-900 text-white text-[8px] md:text-[10px] font-black rounded-lg hover:bg-indigo-600 transition shadow-lg"
                           >
                             이동
                           </button>
                        </div>
                        <p className="text-[10px] md:text-xs text-gray-600 line-clamp-2 leading-relaxed">Q. {q.content}</p>
                     </div>
                   ))}
                </div>
             </div>
          </div>
        </div>
      )}

      {activeMainTab === 'categories' && (
        <div className="bg-white rounded-3xl md:rounded-[56px] p-6 md:p-12 shadow-2xl border border-gray-100 animate-in slide-in-from-bottom-8">
          <h2 className="text-xl md:text-2xl font-black italic uppercase mb-6 md:mb-8">Category Management</h2>
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 mb-8 md:mb-10">
            <input 
              type="text" 
              value={newCatInput}
              onChange={e => setNewCatInput(e.target.value)}
              placeholder="새 카테고리 이름" 
              className="flex-1 p-4 md:p-5 bg-gray-50 rounded-2xl md:rounded-3xl font-black outline-none border-none focus:ring-2 focus:ring-indigo-600 shadow-inner text-sm md:text-base" 
            />
            <button 
              onClick={() => {
                if (newCatInput && !categories.includes(newCatInput)) {
                  onUpdateCategories([...categories, newCatInput]);
                  setNewCatInput('');
                }
              }} 
              className="px-8 md:px-10 py-4 md:py-5 bg-indigo-600 text-white rounded-2xl md:rounded-3xl font-black hover:scale-105 transition shadow-lg text-sm md:text-base"
            >
              추가하기
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {categories.map(cat => (
              <div key={cat} className="p-4 md:p-6 bg-gray-50 rounded-2xl md:rounded-[28px] flex justify-between items-center group border border-gray-100 hover:border-indigo-200 transition">
                <span className="font-black text-xs md:text-base text-gray-900">{cat}</span>
                {cat !== 'ALL' && (
                  <button 
                    onClick={async () => {
                      if(await window.customConfirm(`'${cat}' 카테고리를 삭제하시겠습니까?`)) {
                        onUpdateCategories(categories.filter(c => c !== cat));
                      }
                    }} 
                    className="text-red-300 hover:text-red-500 transition-colors p-1"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12" strokeWidth={3}/></svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeMainTab === 'orders' && (
        <div className="space-y-6 md:space-y-8 animate-in slide-in-from-bottom-8">
          {editingOrder ? (
            <div className="bg-white rounded-3xl md:rounded-[64px] p-6 md:p-12 shadow-2xl border border-gray-100 animate-in zoom-in-95">
              <div className="flex justify-between items-center mb-8 md:mb-10 pb-4 md:pb-6 border-b border-gray-100">
                <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tighter">Order Modification</h2>
                <button onClick={() => setEditingOrder(null)} className="p-2 hover:bg-gray-100 rounded-full transition"><svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12" strokeWidth={3}/></svg></button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10">
                <div className="lg:col-span-1 space-y-6 bg-gray-50 p-6 md:p-8 rounded-2xl md:rounded-[32px]">
                  <h3 className="text-[10px] md:text-sm font-black text-gray-400 uppercase tracking-widest mb-4">Basic Info</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-[9px] md:text-[10px] font-black text-gray-400 block mb-2 uppercase">수령인</label>
                      <input type="text" value={editingOrder.userName || ''} onChange={e => setEditingOrder({...editingOrder, userName: e.target.value})} className="w-full p-3 md:p-4 bg-white border border-gray-100 rounded-xl md:rounded-2xl outline-none font-bold text-sm shadow-sm focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="text-[9px] md:text-[10px] font-black text-gray-400 block mb-2 uppercase">연락처</label>
                      <input type="tel" value={editingOrder.userPhone || ''} onChange={e => setEditingOrder({...editingOrder, userPhone: e.target.value})} className="w-full p-3 md:p-4 bg-white border border-gray-100 rounded-xl md:rounded-2xl outline-none font-bold text-sm shadow-sm focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    <div>
                      <label className="text-[9px] md:text-[10px] font-black text-gray-400 block mb-2 uppercase">배송지</label>
                      <div className="space-y-2">
                        <input type="text" value={editingOrder.userAddress || ''} onChange={e => setEditingOrder({...editingOrder, userAddress: e.target.value})} className="w-full p-3 md:p-4 bg-white border border-gray-100 rounded-xl md:rounded-2xl outline-none font-bold text-xs shadow-sm focus:ring-2 focus:ring-indigo-500" placeholder="주소" />
                        <input type="text" value={editingOrder.userDetailAddress || ''} onChange={e => setEditingOrder({...editingOrder, userDetailAddress: e.target.value})} className="w-full p-3 md:p-4 bg-white border border-gray-100 rounded-xl md:rounded-2xl outline-none font-bold text-xs shadow-sm focus:ring-2 focus:ring-indigo-500" placeholder="상세주소" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[9px] md:text-[10px] font-black text-gray-400 block mb-2 uppercase">상태</label>
                      <select value={editingOrder.status} onChange={e => setEditingOrder({...editingOrder, status: e.target.value as OrderStatus})} className="w-full p-3 md:p-4 bg-indigo-600 text-white rounded-xl md:rounded-2xl font-black outline-none shadow-xl cursor-pointer text-sm">
                        <option value="PAYMENT_COMPLETE">결제완료</option>
                        <option value="PREPARING">상품준비</option>
                        <option value="SHIPPING">배송중</option>
                        <option value="DELIVERED">배송완료</option>
                        <option value="CANCELED">주문취소</option>
                      </select>
                    </div>
                  </div>
                  <button onClick={handleSaveOrder} className="w-full py-4 md:py-5 bg-gray-900 text-white rounded-xl md:rounded-2xl font-black text-xs md:text-sm hover:bg-gray-800 transition shadow-xl mt-6">저장</button>
                </div>
                <div className="lg:col-span-2 space-y-6">
                  <h3 className="text-[10px] md:text-sm font-black text-gray-400 uppercase tracking-widest mb-4">Items Detail</h3>
                  <div className="space-y-3 md:space-y-4 max-h-[400px] md:max-h-[600px] overflow-y-auto pr-2 scrollbar-hide">
                    {(editingOrder.items || []).map((it) => (
                      <div key={it.cartId} className="bg-white border border-gray-100 rounded-2xl md:rounded-[32px] p-4 md:p-6 shadow-sm flex items-center justify-between">
                         <div className="flex items-center gap-3 md:gap-4">
                            <img src={it.image} className="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl object-cover" />
                            <div>
                               <p className="font-black text-gray-900 text-xs md:text-base">{it.name}</p>
                               <p className="text-[10px] md:text-xs text-indigo-600 font-bold">{it.quantity}개</p>
                            </div>
                         </div>
                         <div className="text-right">
                            <p className="text-sm md:text-lg font-black italic">{(it.price * it.quantity).toLocaleString()}원</p>
                         </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-3xl md:rounded-[56px] border border-gray-100 overflow-hidden shadow-2xl">
              <div className="p-6 md:p-10 border-b border-gray-50 flex flex-col md:flex-row justify-between items-start md:items-center bg-gray-50/50 gap-4">
                 <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tighter">Orders & Delivery</h2>
                 <div className="flex flex-wrap gap-1.5 md:gap-2">
                    {['ALL', 'PAYMENT_COMPLETE', 'PREPARING', 'SHIPPING', 'DELIVERED'].map(f => (
                      <button key={f} onClick={() => setOrderFilter(f as any)} 
                        className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl text-[8px] md:text-[10px] font-black transition ${orderFilter === f ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-gray-400 border border-gray-100'}`}>
                        {f === 'ALL' ? '전체' : f === 'PAYMENT_COMPLETE' ? '결제완료' : f === 'PREPARING' ? '준비' : f === 'SHIPPING' ? '배송중' : '완료'}
                      </button>
                    ))}
                 </div>
              </div>

              {/* Mobile View (Card List) */}
              <div className="block md:hidden bg-gray-50 divide-y divide-gray-100">
                {displayedOrders.map(o => (
                  <div key={o.id} className="p-6 space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[8px] font-black text-gray-400 uppercase">{new Date(o.createdAt).toLocaleDateString()}</p>
                        <p className="font-black text-gray-900 text-xs mt-0.5">{o.orderNumber}</p>
                      </div>
                      <select 
                        value={o.status}
                        onChange={(e) => onUpdateOrderStatus(o.id, e.target.value as OrderStatus)}
                        className="p-1.5 rounded-lg text-[10px] font-black border border-gray-200 bg-white outline-none"
                      >
                        <option value="PAYMENT_COMPLETE">결제완료</option>
                        <option value="PREPARING">상품준비</option>
                        <option value="SHIPPING">배송중</option>
                        <option value="DELIVERED">배송완료</option>
                        <option value="CANCELED">주문취소</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                       <div className="flex items-center justify-between">
                          <p className="text-xs font-black text-gray-800">{o.userName} <span className="font-medium text-gray-400 ml-1">({o.userPhone})</span></p>
                          <p className="text-indigo-600 font-black text-sm">{o.totalAmount.toLocaleString()}원</p>
                       </div>
                       <div className="mt-2 bg-indigo-50/50 p-3 rounded-lg border border-indigo-100">
                          <p className="text-[10px] font-bold text-gray-700 leading-relaxed break-keep">
                            {o.userAddress} {o.userDetailAddress}
                          </p>
                       </div>
                    </div>
                    <div className="bg-white p-3 rounded-xl border border-gray-100 space-y-2">
                       {o.items.map((item, idx) => (
                         <div key={idx} className="flex justify-between items-center text-[10px]">
                           <span className="font-bold text-gray-600 line-clamp-1 flex-1">{item.name}</span>
                           <span className="text-indigo-600 font-black ml-2">x{item.quantity}</span>
                         </div>
                       ))}
                    </div>
                    <div className="flex gap-2">
                       <button onClick={() => setEditingOrder(o)} className="flex-1 py-2 bg-white border border-gray-200 rounded-lg text-[10px] font-black text-gray-500">주문수정</button>
                       {o.status === 'PREPARING' && (
                         <button onClick={() => setTrackingForm({ id: o.id, company: CARRIERS[0], number: '' })} className="flex-1 py-2 bg-gray-900 text-white rounded-lg text-[10px] font-black">운송장입력</button>
                       )}
                       {o.trackingNumber && (
                          <button onClick={() => window.open(getTrackingUrl(o.trackingCompany || '', o.trackingNumber) || '#', '_blank')} className="flex-1 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black">배송추적</button>
                       )}
                    </div>
                    {trackingForm?.id === o.id && (
                      <div className="p-4 bg-indigo-50 rounded-xl space-y-3 mt-4 border border-indigo-100 shadow-inner">
                         <select value={trackingForm.company} onChange={e => setTrackingForm({...trackingForm, company: e.target.value})} className="w-full p-2 rounded-lg text-[10px] font-black border-none">
                            {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                         </select>
                         <input type="text" placeholder="번호" value={trackingForm.number} onChange={e => setTrackingForm({...trackingForm, number: e.target.value})} className="w-full p-2 rounded-lg text-[10px] border-none" />
                         <div className="flex gap-1.5">
                            <button onClick={() => setTrackingForm(null)} className="flex-1 py-1.5 bg-white text-gray-400 rounded-lg text-[10px] font-black">취소</button>
                            <button onClick={() => handleApplyTracking(o.id)} className="flex-1 py-1.5 bg-indigo-600 text-white rounded-lg text-[10px] font-black">저장</button>
                         </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop View (Table) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50/80 font-black text-gray-400 text-[10px] uppercase tracking-widest">
                    <tr>
                      <th className="px-8 py-6">주문 정보</th>
                      <th className="px-8 py-6 w-[300px]">고객 / 배송지</th>
                      <th className="px-8 py-6">주문 품목 (상세)</th>
                      <th className="px-8 py-6">상태 및 운송장</th>
                      <th className="px-8 py-6 text-right">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {displayedOrders.map(o => (
                      <tr key={o.id} className="hover:bg-gray-50/20 transition text-sm">
                        <td className="px-8 py-7 align-top">
                          <p className="font-black text-gray-900">{o.orderNumber}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{o.createdAt.replace('T', ' ').split('.')[0]}</p>
                        </td>
                        <td className="px-8 py-7 align-top">
                          <div className="flex items-center gap-2 mb-2">
                             <p className="font-black text-gray-800">{o.userName}</p>
                             <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{o.userPhone}</span>
                          </div>
                          <div className="relative group/addr">
                            <div className="p-2 bg-indigo-50/30 rounded-lg border border-indigo-100 hover:border-indigo-300 transition-colors">
                               <p className="text-[11px] text-gray-600 font-bold leading-relaxed break-keep select-all">
                                 {o.userAddress} {o.userDetailAddress}
                               </p>
                            </div>
                            <button 
                              onClick={async () => {
                                await navigator.clipboard.writeText(`${o.userAddress} ${o.userDetailAddress}`);
                                await window.customAlert('주소가 복사되었습니다.');
                              }}
                              className="absolute top-1 right-1 opacity-0 group-hover/addr:opacity-100 transition-opacity bg-white border border-gray-200 text-[9px] font-black text-indigo-600 px-1.5 py-0.5 rounded shadow-sm hover:bg-indigo-50"
                            >
                              COPY
                            </button>
                          </div>
                        </td>
                        <td className="px-8 py-7 align-top">
                           <div className="max-h-32 overflow-y-auto space-y-2 pr-2 scrollbar-hide">
                              {o.items.map((item, idx) => (
                                <div key={idx} className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex justify-between">
                                   <p className="text-[11px] font-black text-gray-800 line-clamp-1">{item.name}</p>
                                   <span className="text-[11px] text-indigo-600 font-black whitespace-nowrap ml-2">x{item.quantity}</span>
                                </div>
                              ))}
                           </div>
                        </td>
                        <td className="px-8 py-7 align-top">
                          <div className="flex flex-col gap-3">
                            <select 
                              value={o.status}
                              onChange={(e) => onUpdateOrderStatus(o.id, e.target.value as OrderStatus)}
                              className={`p-2 rounded-xl text-[11px] font-black border border-gray-200 outline-none ${
                                o.status === 'PAYMENT_COMPLETE' ? 'bg-blue-50 text-blue-600' :
                                o.status === 'SHIPPING' ? 'bg-indigo-600 text-white' :
                                o.status === 'DELIVERED' ? 'bg-gray-900 text-white' :
                                o.status === 'CANCELED' ? 'bg-red-50 text-red-500' : 'bg-white'
                              }`}
                            >
                              <option value="PAYMENT_COMPLETE">결제완료</option>
                              <option value="PREPARING">상품준비</option>
                              <option value="SHIPPING">배송중</option>
                              <option value="DELIVERED">배송완료</option>
                              <option value="CANCELED">주문취소</option>
                            </select>
                            {o.trackingNumber && (
                              <p className="text-[10px] text-indigo-600 font-bold bg-indigo-50 px-2 py-1 rounded-lg text-center">{o.trackingCompany}<br/>{o.trackingNumber}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-8 py-7 text-right align-top">
                          <button onClick={() => setEditingOrder(o)} className="p-3 bg-white border border-gray-100 rounded-2xl hover:bg-indigo-600 hover:text-white transition shadow-sm">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredOrders.length > visibleOrdersCount && (
                <div className="p-6 md:p-10 border-t border-gray-50 flex flex-col items-center bg-gray-50/20">
                   <button 
                     onClick={() => setVisibleOrdersCount(prev => prev + 10)}
                     className="px-8 md:px-10 py-3 md:py-4 bg-white border border-gray-200 text-gray-900 rounded-xl md:rounded-2xl font-black text-[10px] md:text-xs hover:border-indigo-600 hover:text-indigo-600 transition shadow-sm"
                   >
                     더보기 (10개씩)
                   </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeMainTab === 'products' && (
        <div className="space-y-6 md:space-y-10 animate-in slide-in-from-bottom-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 md:p-10 rounded-3xl md:rounded-[48px] border border-gray-50 shadow-sm gap-6">
            <div>
              <h2 className="text-xl md:text-3xl font-black italic tracking-tighter uppercase">Product List</h2>
              <p className="text-[10px] md:text-sm font-bold text-gray-400 mt-1 md:mt-2">{processedProducts.length}개의 상품 관리</p>
            </div>
            <div className="flex flex-wrap gap-2 md:gap-3 w-full md:w-auto items-center">
              <div className="relative group w-full md:w-64">
                  <input 
                      type="text" 
                      placeholder="상품명 또는 ID 검색" 
                      value={productSearchQuery}
                      onChange={(e) => setProductSearchQuery(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl md:rounded-[20px] text-xs font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition shadow-inner"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  </div>
              </div>

              <input type="file" ref={excelInputRef} className="hidden" accept=".csv" onChange={handleImportCSV} />
              <button onClick={() => excelInputRef.current?.click()} className="flex-1 md:flex-none px-4 py-3 bg-white border-2 border-gray-100 text-gray-900 rounded-xl md:rounded-[20px] text-[10px] md:text-xs font-black hover:border-indigo-600 transition flex items-center justify-center gap-2">
                업로드
              </button>
              <button onClick={handleExportCSV} className="flex-1 md:flex-none px-4 py-3 bg-indigo-50 text-indigo-600 rounded-xl md:rounded-[20px] text-[10px] md:text-xs font-black hover:bg-indigo-100 transition flex items-center justify-center gap-2">
                다운로드
              </button>
              <button onClick={() => handleStartEditProduct({ categories: [], options: [], addOns: [], price: 0, originalPrice: 0, stock: 10, shippingFee: 3000, description: '', isNew: true })} className="w-full md:w-auto px-6 py-3 bg-gray-900 text-white rounded-xl md:rounded-[20px] text-[10px] md:text-xs font-black hover:bg-indigo-600 transition shadow-xl">
                새 상품 등록
              </button>
            </div>
          </div>

          {editingProduct ? (
            <div className="bg-white rounded-3xl md:rounded-[64px] p-6 md:p-16 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-500">
              <div className="flex justify-between items-center mb-8 md:mb-12 pb-6 md:pb-8 border-b border-gray-100">
                 <h3 className="text-xl md:text-3xl font-black italic tracking-tighter uppercase">{editingProduct.id ? 'Edit Product' : 'New Item'}</h3>
                 <button onClick={() => setEditingProduct(null)} className="p-2 hover:bg-gray-100 rounded-full transition"><svg className="w-6 h-6 md:w-8 md:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12" strokeWidth={3}/></svg></button>
              </div>

              <div className="flex flex-col gap-8 md:gap-12 mb-10">
                <div className="space-y-6 md:space-y-8">
                  <div className="space-y-4">
                    <div>
                      <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">상품명</label>
                      <input type="text" value={editingProduct.name || ''} onChange={e => setEditingProduct({...editingProduct, name: e.target.value})} className="w-full p-4 md:p-6 bg-gray-50 border-none rounded-2xl md:rounded-3xl outline-none font-black text-base md:text-xl shadow-inner focus:ring-2 focus:ring-indigo-600" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">정가</label>
                        <input type="number" value={editingProduct.originalPrice || 0} onChange={e => setEditingProduct({...editingProduct, originalPrice: Number(e.target.value)})} className="w-full p-4 md:p-6 bg-gray-50 border-none rounded-2xl md:rounded-3xl outline-none font-black text-sm md:text-lg" />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">판매가</label>
                        <input type="number" value={editingProduct.price || 0} onChange={e => setEditingProduct({...editingProduct, price: Number(e.target.value)})} className="w-full p-4 md:p-6 bg-white border-2 border-indigo-100 rounded-2xl md:rounded-3xl outline-none font-black text-sm md:text-lg" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">재고</label>
                        <input type="number" value={editingProduct.stock || 0} onChange={e => setEditingProduct({...editingProduct, stock: Number(e.target.value)})} className="w-full p-4 md:p-6 bg-gray-50 border-none rounded-2xl md:rounded-3xl outline-none font-black text-sm md:text-lg" />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">배송비</label>
                        <input type="number" value={editingProduct.shippingFee || 0} onChange={e => setEditingProduct({...editingProduct, shippingFee: Number(e.target.value)})} className="w-full p-4 md:p-6 bg-gray-50 border-none rounded-2xl md:rounded-3xl outline-none font-black text-sm md:text-lg" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <input 
                        type="checkbox" 
                        id="isNewCheck" 
                        checked={!!editingProduct.isNew} 
                        onChange={e => setEditingProduct({...editingProduct, isNew: e.target.checked})}
                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                      <label htmlFor="isNewCheck" className="text-xs font-bold text-gray-700 cursor-pointer select-none">신상품 (NEW 배지 표시)</label>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                   <div>
                    <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">이미지 URL</label>
                    <input type="text" value={editingProduct.image || ''} onChange={e => setEditingProduct({...editingProduct, image: e.target.value})} className="w-full p-4 bg-gray-50 border-none rounded-2xl md:rounded-3xl outline-none font-bold text-xs shadow-inner" />
                   </div>
                   <div>
                    <label className="text-[10px] font-black text-gray-400 block mb-4 uppercase">상세 설명</label>
                    <WebEditor value={editingProduct.description || ''} onChange={html => setEditingProduct({...editingProduct, description: html})} />
                   </div>
                   <div>
                    <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">카테고리</label>
                    <div className="flex flex-wrap gap-2">
                      {categories.filter(c => c !== 'ALL').map(cat => (
                        <button 
                          key={cat} 
                          onClick={() => {
                            const currentCats = editingProduct.categories || [];
                            const newCats = currentCats.includes(cat) ? currentCats.filter(c => c !== cat) : [...currentCats, cat];
                            setEditingProduct({...editingProduct, categories: newCats});
                          }}
                          className={`px-3 py-1 rounded-full text-[10px] font-black transition ${editingProduct.categories?.includes(cat) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'}`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                   </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-8 md:pt-10 mb-8 md:mb-10">
                 <div className="flex justify-between items-center mb-6">
                    <h4 className="text-sm md:text-base font-black uppercase tracking-widest text-gray-900">Options</h4>
                    <button onClick={addOption} className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-black hover:bg-indigo-100 transition">+ 옵션 그룹 추가</button>
                 </div>
                 <div className="space-y-6 mb-10">
                    {(editingProduct.options || []).map((opt, i) => (
                      <div key={i} className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                         <div className="flex gap-4 mb-4">
                            <input type="text" placeholder="옵션명 (예: 색상)" value={opt.name} onChange={e => {
                               const newOpts = [...(editingProduct.options || [])];
                               newOpts[i].name = e.target.value;
                               setEditingProduct({...editingProduct, options: newOpts});
                            }} className="flex-1 p-3 rounded-xl border border-gray-200 text-sm font-bold" />
                            <button onClick={() => removeOption(i)} className="text-red-400 hover:text-red-600 p-2 font-black">삭제</button>
                         </div>
                         <div className="space-y-2 pl-4 border-l-2 border-indigo-100">
                            {opt.values.map((val, j) => (
                              <div key={j} className="flex gap-2">
                                 <input type="text" placeholder="값 (예: 빨강)" value={val.name} onChange={e => {
                                   const newOpts = [...(editingProduct.options || [])];
                                   newOpts[i].values[j].name = e.target.value;
                                   setEditingProduct({...editingProduct, options: newOpts});
                                 }} className="flex-1 p-2 rounded-lg border border-gray-200 text-xs" />
                                 <input type="number" placeholder="추가금" value={val.extraPrice} onChange={e => {
                                   const newOpts = [...(editingProduct.options || [])];
                                   newOpts[i].values[j].extraPrice = Number(e.target.value);
                                   setEditingProduct({...editingProduct, options: newOpts});
                                 }} className="w-24 p-2 rounded-lg border border-gray-200 text-xs" />
                                 <button onClick={() => removeOptionValue(i, j)} className="text-red-300 hover:text-red-500 text-xs">x</button>
                              </div>
                            ))}
                            <button onClick={() => addOptionValue(i)} className="text-xs font-black text-indigo-500 hover:text-indigo-700 py-1">+ 값 추가</button>
                         </div>
                      </div>
                    ))}
                 </div>

                 <div className="flex justify-between items-center mb-6">
                    <h4 className="text-sm md:text-base font-black uppercase tracking-widest text-gray-900">Add-ons</h4>
                    <button onClick={addAddon} className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-black hover:bg-indigo-100 transition">+ 추가상품 추가</button>
                 </div>
                 <div className="space-y-3">
                    {(editingProduct.addOns || []).map((add, i) => (
                      <div key={i} className="flex gap-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                         <input type="text" placeholder="상품명" value={add.name} onChange={e => {
                            const newAdds = [...(editingProduct.addOns || [])];
                            newAdds[i].name = e.target.value;
                            setEditingProduct({...editingProduct, addOns: newAdds});
                         }} className="flex-1 p-3 rounded-xl border border-gray-200 text-sm font-bold" />
                         <input type="number" placeholder="가격" value={add.price} onChange={e => {
                            const newAdds = [...(editingProduct.addOns || [])];
                            newAdds[i].price = Number(e.target.value);
                            setEditingProduct({...editingProduct, addOns: newAdds});
                         }} className="w-32 p-3 rounded-xl border border-gray-200 text-sm font-bold" />
                         <button onClick={() => removeAddon(i)} className="text-red-400 hover:text-red-600 p-2 font-black">삭제</button>
                      </div>
                    ))}
                 </div>
              </div>

              <div className="flex justify-end gap-4 border-t border-gray-100 pt-8">
                 <button onClick={() => setEditingProduct(null)} className="px-8 py-4 bg-gray-100 text-gray-500 font-black rounded-2xl hover:bg-gray-200 transition text-xs uppercase tracking-widest">취소</button>
                 <button onClick={handleSaveProduct} className="px-12 py-4 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition shadow-xl text-xs uppercase tracking-widest">저장하기</button>
              </div>
            </div>
          ) : (
             <div className="bg-white rounded-3xl md:rounded-[56px] border border-gray-100 overflow-hidden shadow-2xl">
              {/* Mobile View (Card List) */}
              <div className="block md:hidden bg-gray-50 divide-y divide-gray-100">
                 {displayedProducts.map(p => {
                   const hasDiscount = p.originalPrice && p.originalPrice > p.price;
                   const discountRate = hasDiscount ? Math.round(((p.originalPrice! - p.price) / p.originalPrice!) * 100) : 0;
                   return (
                   <div key={p.id} className="p-5 flex gap-4 bg-white">
                      <img src={p.image} className="w-20 h-20 rounded-xl object-cover border border-gray-100" />
                      <div className="flex-1 flex flex-col justify-between">
                         <div>
                            <p className="text-xs font-black text-gray-900 line-clamp-1">{p.name}</p>
                            <p className="text-[9px] text-gray-400 font-bold mt-0.5">{p.categories.join(', ')}</p>
                            <div className="flex flex-col mt-2">
                               {hasDiscount && (
                                 <span className="text-[9px] text-gray-300 line-through font-bold">{p.originalPrice?.toLocaleString()}원</span>
                               )}
                               <div className="flex items-center gap-2">
                                  <span className={`text-xs font-black ${hasDiscount ? 'text-blue-600' : 'text-indigo-600'}`}>{p.price.toLocaleString()}원</span>
                                  {hasDiscount && <span className="text-[9px] font-black text-blue-600">{discountRate}% OFF</span>}
                                  <span className="text-[10px] text-gray-400">재고: {p.stock}</span>
                               </div>
                            </div>
                         </div>
                         <div className="flex gap-2 mt-3">
                            <button onClick={() => handleStartEditProduct(p)} className="flex-1 py-1.5 bg-gray-100 text-[10px] font-black text-gray-500 rounded-lg">수정</button>
                            <button onClick={() => handleFullDeleteProduct(p.id)} className="flex-1 py-1.5 bg-red-50 text-[10px] font-black text-red-500 rounded-lg">삭제</button>
                         </div>
                      </div>
                   </div>
                 )})}
              </div>

              {/* Desktop View (Table) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50/80 font-black text-gray-400 text-[11px] uppercase tracking-widest">
                    <tr>
                      <th className="px-10 py-7">Product Info</th>
                      <th className="px-10 py-7">Price & Options</th>
                      <th className="px-10 py-7 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {displayedProducts.map(p => {
                      const hasDiscount = p.originalPrice && p.originalPrice > p.price;
                      const discountRate = hasDiscount ? Math.round(((p.originalPrice! - p.price) / p.originalPrice!) * 100) : 0;
                      return (
                      <tr key={p.id} className="hover:bg-gray-50/30 transition">
                        <td className="px-10 py-8">
                          <div className="flex gap-5 items-center">
                            <img src={p.image} className="w-16 h-16 rounded-2xl object-cover border border-gray-100" />
                            <div>
                              <p className="font-black text-gray-900">{p.name}</p>
                              <p className="text-[10px] text-gray-400 font-bold">{p.categories.join(', ')}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-10 py-8">
                          <div className="flex flex-col">
                             {hasDiscount && (
                               <span className="text-[10px] text-gray-300 line-through font-bold mb-0.5">{p.originalPrice?.toLocaleString()}원</span>
                             )}
                             <div className="flex items-center gap-2">
                                <span className={`font-black ${hasDiscount ? 'text-blue-600' : 'text-gray-900'}`}>{p.price.toLocaleString()}원</span>
                                {hasDiscount && (
                                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[9px] font-black rounded uppercase">{discountRate}% OFF</span>
                                )}
                             </div>
                             <span className="text-[10px] text-gray-400 mt-1">재고: {p.stock}개 / 배송비: {p.shippingFee.toLocaleString()}원</span>
                          </div>
                        </td>
                        <td className="px-10 py-8 text-right">
                          <div className="flex gap-3 justify-end">
                            <button onClick={() => handleStartEditProduct(p)} className="p-3 bg-white border border-gray-100 rounded-xl text-indigo-600 hover:bg-indigo-50 shadow-sm"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                            <button onClick={() => handleFullDeleteProduct(p.id)} className="p-3 bg-white border border-gray-100 rounded-xl text-red-500 hover:bg-red-50 shadow-sm"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>

              {products.length > visibleProductsCount && (
                <div className="p-6 md:p-10 border-t border-gray-50 flex flex-col items-center bg-gray-50/20">
                   <button 
                     onClick={() => setVisibleProductsCount(prev => prev + 50)}
                     className="px-8 md:px-10 py-3 md:py-4 bg-white border border-gray-200 text-gray-900 rounded-xl md:rounded-2xl font-black text-[10px] md:text-xs hover:border-indigo-600 hover:text-indigo-600 transition shadow-sm"
                   >
                     상품 더 보기
                   </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeMainTab === 'users' && (
        <div className="bg-white rounded-3xl md:rounded-[56px] border border-gray-100 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8">
           <div className="p-6 md:p-10 border-b border-gray-50 bg-gray-50/50 flex justify-between items-center">
              <h2 className="text-xl md:text-2xl font-black italic uppercase tracking-tighter">User Management</h2>
              <span className="px-4 py-1 bg-emerald-100 text-emerald-600 rounded-full text-[10px] font-black uppercase">Total: {users.length}</span>
           </div>
           
           {editingMember ? (
             <div className="p-6 md:p-12">
                <h3 className="text-lg font-black mb-6">회원 정보 수정</h3>
                <div className="space-y-4 max-w-xl">
                   <div>
                      <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">이름</label>
                      <input type="text" value={editingMember.name} onChange={e => setEditingMember({...editingMember, name: e.target.value})} className="w-full p-3 bg-gray-50 rounded-xl text-sm font-bold border-none" />
                   </div>
                   <div>
                      <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">연락처</label>
                      <input type="text" value={editingMember.phone} onChange={e => setEditingMember({...editingMember, phone: e.target.value})} className="w-full p-3 bg-gray-50 rounded-xl text-sm font-bold border-none" />
                   </div>
                   <div>
                      <label className="text-[10px] font-black text-gray-400 block mb-2 uppercase">주소</label>
                      <input type="text" value={editingMember.address} onChange={e => setEditingMember({...editingMember, address: e.target.value})} className="w-full p-3 bg-gray-50 rounded-xl text-sm font-bold border-none mb-2" />
                      <input type="text" value={editingMember.detailAddress || ''} onChange={e => setEditingMember({...editingMember, detailAddress: e.target.value})} className="w-full p-3 bg-gray-50 rounded-xl text-sm font-bold border-none" />
                   </div>
                   <div className="flex gap-2 pt-4">
                      <button onClick={handleSaveMember} className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-black text-xs hover:bg-indigo-700">저장</button>
                      <button onClick={() => setEditingMember(null)} className="flex-1 py-3 bg-gray-200 text-gray-600 rounded-xl font-black text-xs hover:bg-gray-300">취소</button>
                   </div>
                </div>
             </div>
           ) : (
             <>
               {/* Mobile View (Card List) */}
               <div className="block md:hidden bg-gray-50 divide-y divide-gray-100">
                 {users.map(u => (
                   <div key={u.id} className="p-6 space-y-4 bg-white">
                      <div className="flex justify-between items-start">
                         <div>
                            <p className="text-sm font-black text-gray-900 uppercase italic">{u.name}</p>
                            <p className="text-[10px] text-gray-500 font-bold mt-0.5">{u.email}</p>
                            <p className="text-[10px] text-indigo-600 font-bold mt-0.5">{u.phone}</p>
                         </div>
                         <p className="text-sm font-black text-indigo-600 italic">{u.points.toLocaleString()}P</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <p className="text-[10px] text-gray-600 font-medium leading-relaxed">{u.address} {u.detailAddress}</p>
                      </div>
                      <div className="flex gap-2">
                         <button onClick={async () => {
                             const newP = await window.customPrompt('회원의 포인트를 수정합니다:', String(u.points));
                             if (newP !== null) onUpdateUserPoints(u.id, Number(newP));
                         }} className="flex-1 py-2.5 bg-gray-100 text-[10px] font-black text-gray-600 rounded-xl">포인트 조정</button>
                         <button onClick={() => setEditingMember(u)} className="flex-1 py-2.5 bg-indigo-50 border border-indigo-100 rounded-xl text-indigo-600 text-[10px] font-black">정보 수정</button>
                         <button onClick={async () => { if(await window.customConfirm(`${u.name}님을 탈퇴 처리하시겠습니까?`)) onDeleteUser(u.id); }} className="flex-1 py-2.5 bg-red-50 border border-red-100 rounded-xl text-red-500 text-[10px] font-black">회원 탈퇴</button>
                      </div>
                   </div>
                 ))}
               </div>

               {/* Desktop View (Table) */}
               <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      <tr>
                         <th className="px-8 py-5">가입일</th>
                         <th className="px-8 py-5">사용자</th>
                         <th className="px-8 py-5">연락처/주소</th>
                         <th className="px-8 py-5">포인트</th>
                         <th className="px-8 py-5 text-right">관리</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                       {users.map(u => (
                         <tr key={u.id} className="hover:bg-gray-50/30 transition group">
                            <td className="px-8 py-6 text-xs font-bold text-gray-400">{new Date(u.joinedAt).toLocaleDateString()}</td>
                            <td className="px-8 py-6">
                               <p className="font-black text-gray-900">{u.name}</p>
                               <p className="text-[10px] text-gray-400">{u.email}</p>
                               <p className="text-[9px] text-gray-300">ID: {u.id}</p>
                            </td>
                            <td className="px-8 py-6">
                               <p className="text-xs font-bold text-gray-600">{u.phone}</p>
                               <p className="text-[10px] text-gray-400 truncate max-w-[200px]">{u.address} {u.detailAddress}</p>
                            </td>
                            <td className="px-8 py-6">
                               <div className="flex items-center gap-2">
                                  <span className="font-black text-indigo-600">{u.points.toLocaleString()} P</span>
                                  <button 
                                    onClick={async () => {
                                      const p = await window.customPrompt('수정할 포인트:', String(u.points));
                                      if(p !== null) onUpdateUserPoints(u.id, Number(p));
                                    }}
                                    className="text-[9px] bg-gray-100 px-2 py-0.5 rounded hover:bg-gray-200 transition"
                                  >
                                    수정
                                  </button>
                               </div>
                            </td>
                            <td className="px-8 py-6 text-right">
                               <button onClick={() => setEditingMember(u)} className="text-[10px] font-black text-indigo-500 hover:text-indigo-700 mr-3">정보수정</button>
                               <button 
                                 onClick={async () => {
                                   if(await window.customConfirm(`정말 ${u.name} 회원을 삭제하시겠습니까?`)) {
                                     onDeleteUser(u.id);
                                   }
                                 }} 
                                 className="text-[10px] font-black text-red-300 hover:text-red-500"
                               >
                                 삭제
                               </button>
                            </td>
                         </tr>
                       ))}
                    </tbody>
                  </table>
               </div>
             </>
           )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
